import {LitElement, ReactiveController} from 'lit';

// Emulates drag&drop events for touch events for the host (considers "draggable" attribute on host)
export class SpTouchDragController implements ReactiveController {
    host: LitElement;
    touchstartListener = this.listenTouchstart.bind(this);
    touchendListener = this.listenTouchend.bind(this);
    touchmoveListener = this.listenTouchmove.bind(this);

    options: SpTouchDragOptions;
    touchTimeoutId;
    touchStarted;
    dragStarted;
    dragStartEvent;
    latestFiredDragEvent;
    clone;

    constructor(host: LitElement, options: SpTouchDragOptions = {
        startDragDelayMs: 500
    }) {
        (this.host = host).addController(this);
        this.options = options;
    }

    hostConnected() {
        this.host.addEventListener('dragstart', e => {
            // If your browser is capable of firing drag events from touch, we need to suppress those otherwise we have double events
            if (this.touchStarted && !e.hasOwnProperty('touchDragController')) {
                e.stopPropagation();
                e.stopImmediatePropagation();
                e.preventDefault();
            }
        });
        this.host.addEventListener('touchstart', this.touchstartListener);
        this.host.addEventListener('touchmove', this.touchmoveListener);
        this.host.addEventListener('touchend', this.touchendListener);
        this.host.addEventListener('touchcancel', this.touchendListener);
    }

    hostDisconnected() {
        this.host.removeEventListener('touchstart', this.touchstartListener);
        this.host.removeEventListener('touchmove', this.touchmoveListener);
        this.host.removeEventListener('touchend', this.touchendListener);
        this.host.removeEventListener('touchcancel', this.touchendListener);
    }

    listenTouchstart(e) {
        this.touchStarted = true;
        const draggable = this.host.getAttribute('draggable');
        if (draggable && draggable === 'true') {
            this.dragStartEvent = e;
            // Start timer as trigger for drag behaviour
            this.touchTimeoutId = setTimeout(() => {
                this.touchTimeoutId = null;
                this.dragStarted = true;
                this.clone = this.deepClone(this.host).firstChild;
                this.clone.style['position'] = 'fixed';
                this.clone.style['zIndex'] = '9999';
                this.clone.style['left'] = `${ e.touches[0].clientX + 5 }px`;
                this.clone.style['top'] = `${ e.touches[0].clientY + 5}px`;
                this.clone.style['width'] = `${ this.host.offsetWidth }px`;
                this.clone.style['height'] = `${ this.host.offsetHeight }px`;
                // Suppress re-rendering of clone by overwriting render function
                this.clone.render = () => {};
                // Attach clone to dom
                document.body.appendChild(this.clone);
                // Create and fire dragstart event
                const dragStartEvent = this.createEvent('dragstart', e, {
                    dataTransfer: new DataTransfer(),
                    target: this.host
                });
                this.host.dispatchEvent(dragStartEvent);
                // Imitate initial dragenter event
                const dragEnterEvent = this.createEvent('dragenter', dragStartEvent, {
                    target: document.body,
                    touchDragController: true
                });
                document.dispatchEvent(dragEnterEvent);
                this.latestFiredDragEvent = dragStartEvent;
            }, this.options?.startDragDelayMs ? this.options.startDragDelayMs : 0)
        }
    }
    listenTouchmove(e) {
        if (this.dragStarted) {
            // Suppress scrolling behavior
            e.preventDefault();
            e.stopPropagation();
            // Let ghost follow cursor
            const x = e.touches[0].clientX; //get x position
            const y = e.touches[0].clientY; //get y position
            this.clone.style['left'] = `${x + 5}px`;
            this.clone.style['top'] = `${y + 5}px`;
            const target = this.nestedElementFromPoint(x, y);
            // Check if dragenter or dragleave events need to be fired
            // Case: Drag is leaving viewport
            if (!target && this.latestFiredDragEvent.type !== 'dragleave') {
                const dragLeaveEvent = this.createEvent('dragleave', e, {
                    target: this.latestFiredDragEvent.target,
                    dataTransfer: this.latestFiredDragEvent.dataTransfer
                });
                this.latestFiredDragEvent.target.dispatchEvent(dragLeaveEvent);
                const dragLeaveDocumentEvent = this.createEvent('dragleave', e, {
                    target: document.body,
                    dataTransfer: this.latestFiredDragEvent.dataTransfer
                });
                document.dispatchEvent(dragLeaveDocumentEvent);
                this.latestFiredDragEvent = dragLeaveEvent;
            }
            // Case: Drag is re-entering viewport
            else if (target && this.latestFiredDragEvent.type === 'dragleave') {
                const dragEnterDocumentEvent = this.createEvent('dragenter', e, {
                    target: document.body,
                    dataTransfer: this.latestFiredDragEvent.dataTransfer
                });
                document.dispatchEvent(dragEnterDocumentEvent);
                const dragEnter = this.createEvent('dragenter', e, {
                    target,
                    dataTransfer: this.latestFiredDragEvent.dataTransfer
                });
                target.dispatchEvent(dragEnter);
                this.latestFiredDragEvent = dragEnter;
            }
            // Case: Drag is moving top different element
            else if (target && target !== this.latestFiredDragEvent.target) {
                const dragLeaveEvent = this.createEvent('dragleave', e, {
                    target: this.latestFiredDragEvent.target,
                    dataTransfer: this.latestFiredDragEvent.dataTransfer
                });
                this.latestFiredDragEvent.target.dispatchEvent(dragLeaveEvent);
                const dragEnterEvent = this.createEvent('dragenter', dragLeaveEvent, {
                    target
                });
                target.dispatchEvent(dragEnterEvent);
                this.latestFiredDragEvent = dragEnterEvent;
            }
            // Case: Drag is moving over same element
            else if (target) {
                // Else fire a dragover event
                const dragOverEvent = this.createEvent('dragover', e, {
                    target,
                    dataTransfer: this.latestFiredDragEvent.dataTransfer
                });
                target.dispatchEvent(dragOverEvent);
                this.latestFiredDragEvent = dragOverEvent;
            }
        }
        // Cancel trigger timeout if moved out of element
        else if (e.target !== this.nestedElementFromPoint(e.touches[0].clientX, e.touches[0].clientY)) {
            clearTimeout(this.touchTimeoutId);
            this.touchTimeoutId = null;
        }
    }

    listenTouchend(e) {
        this.touchStarted = false;
        if (this.touchTimeoutId) {
            clearTimeout(this.touchTimeoutId);
            this.touchTimeoutId = null;
        }
        if (this.dragStarted) {
            this.clone?.remove();
            this.dragStarted = false;
            const x = e.changedTouches[0].clientX;
            const y = e.changedTouches[0].clientY;
            const target = this.nestedElementFromPoint(x, y);
            const dragEndEvent = this.createEvent('dragend', e, {
                target,
                dataTransfer: this.latestFiredDragEvent.dataTransfer,
                x,
                y
            });
            this.host.dispatchEvent(dragEndEvent);
            const dropEvent = this.createEvent('drop', dragEndEvent);
            target.dispatchEvent(dropEvent);
            this.latestFiredDragEvent = null;
        }
    }

    deepClone(host) {
        const cloneNode = (node, parent) => {
            const walkTree = (nextn, nextp) => {
                while (nextn) {
                    cloneNode(nextn, nextp);
                    nextn = nextn.nextSibling;
                }
            };
            const clone = node.cloneNode();
            parent.appendChild(clone);
            if (node.shadowRoot) {
                walkTree(node.shadowRoot.firstChild, clone.attachShadow({ mode: 'open' }));
            }
            walkTree(node.firstChild, clone);
        };

        const fragment = document.createDocumentFragment();
        cloneNode(host, fragment);
        return fragment;
    };

    private createEvent(type: string, baseEvent: any, overrides?: {
        touchDragController?: boolean,
        dataTransfer?: DataTransfer,
        target?: Element,
        x?: number,
        y?: number
        }): DragEvent {
        const x = overrides?.x ? overrides.x : typeof baseEvent.clientX === 'number' ? baseEvent.clientX : baseEvent.touches[0]?.clientX;
        const y = overrides?.y ? overrides.y: typeof baseEvent.clientY === 'number' ? baseEvent.clientY : baseEvent.touches[0]?.clientY;
        const target = overrides?.target ? overrides.target : baseEvent.target;
        let options: DragEventInit = defaultEventOptions(type);
        options.dataTransfer = overrides?.dataTransfer ?
            overrides.dataTransfer :
            baseEvent.dataTransfer;
        let event = new DragEvent(type, options);
        event = Object.defineProperties(event, {
            target: { value: target },
            srcElement: { value: target },
            clientX: { value:  x},
            x: { value:  x},
            y: { value: y },
            clientY: { value:  y},
            view: { value: window }
        });
        return event as unknown as DragEvent;
    }

    nestedElementFromPoint(x, y) {
        if (x < 0 || x > window.innerWidth || y < 0 || y > window.innerHeight) return null;
        const getElement = (x, y, root: Document | ShadowRoot = document) => {
            const elem = root.elementFromPoint(x, y);
            if (!elem) {
                return root;
            } else if (elem && elem.shadowRoot && elem.shadowRoot !== root) {
                return getElement(x, y, elem.shadowRoot);
            } else {
                return elem;
            }
        }
        return getElement(x, y) as Element;
    }
}

const defaultEventOptions = (type: string) => {
    return {
        cancelable: type === 'dragleave' ? false : true,
        composed: true,
        bubbles: type === 'dragleave' || type === 'dragenter' ? false : true
    }
};

export interface SpTouchDragOptions {
    startDragDelayMs?: number
}